import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Checkbox,
  Tooltip,
} from '@mui/material';
import {
  CallMade,
  CallReceived,
  CallMissed,
  HelpOutline,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import MarkAsLeadModal from '../../components/common/MarkAsLeadModal';
import { getCallStatusColor } from '../../utils/helper';
import { LEAD_THEME as LEAD_T } from '../../utils/lead_helper_v2';
import TinyAudioPlayer from '../../components/common/TinyAudioPlayer';
import EmptyCallsPageCard from './EmptyCallsPageCard';
import {
  SmartTableTH,
  SmartTableTD,
  TABLE_THEME,
  TABLE_ROW_CLASS,
  TABLE_ROW_GLOBAL_CSS,
  TABLE_SCROLLBAR_CSS,
} from '../../components/common/SmartTable';

const T = TABLE_THEME;

// ─── Same cell visuals as CallListViewV2 (calls table) ───────────────────────

function CallStatusPill({ status, label }) {
  const colors = getCallStatusColor(status || '-');
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        background: colors.background,
        color: colors.color,
        fontSize: 11,
        fontWeight: 600,
        padding: '3px 9px 3px 7px',
        borderRadius: 99,
        whiteSpace: 'nowrap',
        border: `1px solid ${colors.borderColor}`,
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: '50%',
          background: colors.color,
          flexShrink: 0,
        }}
      />
      {label || status || '—'}
    </span>
  );
}

function getCallTypeIcon(callType) {
  switch (callType) {
    case 'outgoing':
      return { icon: CallMade, color: LEAD_T.green, bg: LEAD_T.greenLt };
    case 'incoming':
      return { icon: CallReceived, color: '#1976d2', bg: '#e3f2fd' };
    case 'missed':
      return { icon: CallMissed, color: '#c62828', bg: '#ffebee' };
    default:
      return { icon: HelpOutline, color: T.muted, bg: T.borderL };
  }
}

function CallDirectionPill({ callType }) {
  const { icon: Icon, color, bg } = getCallTypeIcon(callType);
  const label =
    callType === 'outgoing'
      ? 'Outgoing'
      : callType === 'incoming'
        ? 'Incoming'
        : callType === 'missed'
          ? 'Missed'
          : 'Unknown';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: bg,
        color,
        fontSize: 11,
        fontWeight: 600,
        padding: '4px 10px',
        borderRadius: 99,
        whiteSpace: 'nowrap',
        border: `1px solid ${color}40`,
      }}
    >
      {React.createElement(Icon, { sx: { fontSize: 14 } })}
      {label}
    </span>
  );
}

function AgentChip({ name, assigned, onClick }) {
  const isUnassigned = !assigned || !name || name === 'Unassigned' || name === 'Unknown Agent';
  const displayText = isUnassigned ? 'Unassigned' : name || '—';
  return (
    <span
      role={typeof onClick === 'function' ? 'button' : undefined}
      tabIndex={typeof onClick === 'function' ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (typeof onClick === 'function' && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        background: isUnassigned ? T.borderL : T.greenSf,
        color: isUnassigned ? T.muted : T.ink2,
        fontSize: 11,
        fontWeight: 600,
        padding: '3px 9px 3px 7px',
        borderRadius: 99,
        whiteSpace: 'nowrap',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        cursor: typeof onClick === 'function' ? 'pointer' : 'default',
        border: `1px solid ${isUnassigned ? T.border : 'transparent'}`,
        transition: 'background 0.15s, border-color 0.15s',
      }}
      onMouseEnter={(e) => {
        if (typeof onClick !== 'function') return;
        e.currentTarget.style.background = isUnassigned ? '#e8eeeb' : T.greenLt;
        e.currentTarget.style.borderColor = isUnassigned ? T.muted : T.green;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = isUnassigned ? T.borderL : T.greenSf;
        e.currentTarget.style.borderColor = isUnassigned ? T.border : 'transparent';
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          flexShrink: 0,
          background: isUnassigned ? T.muted : T.green,
        }}
      />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayText}</span>
    </span>
  );
}

function UnlinkedCallStatusCell({ call }) {
  const hasRecording =
    call.recording_url &&
    call.recording_length_str &&
    call.recording_length_str !== '0:00' &&
    !['MSD', 'CAN', 'BSY', 'DEC', 'DNR'].includes(call.status);

  if (!hasRecording) {
    return <CallStatusPill status={call.status} label={call.status_verbose} />;
  }
  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <TinyAudioPlayer
        audioUrl={call.recording_url}
        duration={call.recording_length_str}
        sx={{ width: '100%', maxWidth: 220 }}
      />
    </Box>
  );
}

const UnlinkedCallListView = ({
  calls,
  searchKeyword,
  isSearchActive,
  onCallUpdated,
  onExport,
}) => {
  const [markAsLeadModal, setMarkAsLeadModal] = useState({
    open: false,
    assetContactMap: {},
  });
  const [selectedCalls, setSelectedCalls] = useState([]);

  const callRows = useMemo(() => {
    if (!calls?.calls || !Array.isArray(calls.calls)) return [];
    return calls.calls;
  }, [calls]);

  const totalCount = calls?.pagination?.total_count ?? callRows.length;

  const unassignedIds = useMemo(
    () => callRows.filter((call) => !call.lead?.id).map((call) => call.id),
    [callRows]
  );

  const handleMarkAsLead = (assetIds) => {
    const assetContactMap = {};
    callRows.forEach((call) => {
      if (assetIds.includes(call.id) && call.lead?.name) {
        assetContactMap[call?.lead?.name] = call.lead.name;
      }
    });

    setMarkAsLeadModal({
      open: true,
      assetContactMap,
    });
  };

  const handleMarkAsLeadSuccess = (data) => {
    if (onCallUpdated) {
      onCallUpdated(data);
    }
  };

  const handleCloseMarkAsLeadModal = () => {
    setMarkAsLeadModal({
      open: false,
      assetContactMap: {},
    });
  };

  const handleSelectCall = (callId) => {
    setSelectedCalls((prev) =>
      prev.includes(callId) ? prev.filter((id) => id !== callId) : [...prev, callId]
    );
  };

  const handleSelectAllCalls = () => {
    if (selectedCalls.length === unassignedIds.length) {
      setSelectedCalls([]);
    } else {
      setSelectedCalls(unassignedIds);
    }
  };

  const handleMarkSelectedAsLead = () => {
    if (selectedCalls.length > 0) {
      handleMarkAsLead(selectedCalls);
    }
  };

  const hasRows = callRows.length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, minWidth: 0 }}>
      {selectedCalls.length > 0 && (
        <Box
          sx={{
            mb: 1.5,
            p: 1.5,
            backgroundColor: 'primary.light',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexShrink: 0,
          }}
        >
          <Typography variant="body2" color="white">
            {selectedCalls.length} call{selectedCalls.length !== 1 ? 's' : ''} selected
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={handleMarkSelectedAsLead}
            sx={{
              backgroundColor: 'white',
              color: 'primary.main',
              '&:hover': { backgroundColor: 'grey.100' },
            }}
          >
            Mark as Lead
          </Button>
          <Button variant="text" size="small" onClick={() => setSelectedCalls([])} sx={{ color: 'white' }}>
            Clear Selection
          </Button>
        </Box>
      )}

      {hasRows ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 0,
            overflow: 'hidden',
            border: `1px solid ${T.border}`,
            borderLeft: 'none',
            background: T.white,
            boxShadow: 'none',
          }}
        >
          {/* Match CallListViewV2 table toolbar: title + count + Export */}
          <Box
            sx={{
              px: 1.5,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              borderBottom: `1px solid ${T.border}`,
              background: T.white,
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, color: T.ink2, fontSize: 13, whiteSpace: 'nowrap' }}>
                Unlinked calls
              </Typography>
              <Box
                component="span"
                sx={{
                  ml: 0.5,
                  minWidth: 22,
                  height: 20,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 99,
                  px: 1,
                  fontSize: 11,
                  fontWeight: 800,
                  backgroundColor: T.bg,
                  color: T.muted,
                }}
              >
                {totalCount}
              </Box>
            </Box>

            <Tooltip
              title={
                totalCount > 0
                  ? `Export ${Number(totalCount).toLocaleString()} unlinked calls to CSV`
                  : 'No calls to export'
              }
            >
              <span>
                <Button
                  variant="outlined"
                  startIcon={<ExportIcon />}
                  size="small"
                  onClick={onExport}
                  disabled={!onExport || totalCount === 0}
                  sx={{
                    height: 32,
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: 'none',
                  }}
                >
                  Export
                </Button>
              </span>
            </Tooltip>
          </Box>

          <style>{TABLE_ROW_GLOBAL_CSS}</style>
          <style>{TABLE_SCROLLBAR_CSS}</style>
          <style>{`
            .unlinked-calls-table-wrap thead th { position: sticky; top: 0; z-index: 2; background: ${T.headerBg} !important; box-shadow: 0 1px 0 ${T.border}; }
          `}</style>
          <Box
            className="smart-table-wrap unlinked-calls-table-wrap calls-table-wrap-v2"
            sx={{
              overflowX: 'auto',
              overflowY: 'auto',
              flex: 1,
              minHeight: 0,
            }}
          >
            <table
              style={{
                width: '100%',
                tableLayout: 'auto',
                borderCollapse: 'separate',
                borderSpacing: 0,
                minWidth: 1180,
              }}
            >
              <thead>
                <tr>
                  <SmartTableTH theme={T} width={44}>
                    <Checkbox
                      checked={
                        selectedCalls.length > 0 &&
                        selectedCalls.length === unassignedIds.length &&
                        unassignedIds.length > 0
                      }
                      indeterminate={
                        selectedCalls.length > 0 && selectedCalls.length < unassignedIds.length
                      }
                      onChange={handleSelectAllCalls}
                      size="small"
                    />
                  </SmartTableTH>
                  <SmartTableTH theme={T} width={140}>
                    Call Datetime
                  </SmartTableTH>
                  <SmartTableTH theme={T} width={70}>
                    Duration
                  </SmartTableTH>
                  <SmartTableTH theme={T} width={110}>
                    Call Direction
                  </SmartTableTH>
                  <SmartTableTH theme={T} width={90}>
                    Lead ID
                  </SmartTableTH>
                  <SmartTableTH theme={T} width={160}>
                    Lead Name
                  </SmartTableTH>
                  <SmartTableTH theme={T} width={100}>
                    Lead Stage
                  </SmartTableTH>
                  <SmartTableTH theme={T} width={220}>
                    Call Status
                  </SmartTableTH>
                  <SmartTableTH theme={T} width={120}>
                    Agent
                  </SmartTableTH>
                  <SmartTableTH theme={T} width={110}>
                    Action
                  </SmartTableTH>
                </tr>
              </thead>
              <tbody>
                {callRows.map((call) => (
                  <tr key={call.id} className={TABLE_ROW_CLASS}>
                    <SmartTableTD theme={T}>
                      <Checkbox
                        checked={selectedCalls.includes(call.id)}
                        onChange={() => handleSelectCall(call.id)}
                        size="small"
                      />
                    </SmartTableTD>
                    <SmartTableTD theme={T}>
                      <span
                        style={{
                          fontSize: 12.5,
                          fontWeight: 500,
                          color: T.ink2,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {[call.date, call.time].filter(Boolean).join(' ') || '—'}
                      </span>
                    </SmartTableTD>
                    <SmartTableTD theme={T}>
                      {call.call_duration ? (
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {call.call_duration}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          -
                        </Typography>
                      )}
                    </SmartTableTD>
                    <SmartTableTD theme={T}>
                      {call.call_type ? (
                        <CallDirectionPill callType={call.call_type} />
                      ) : (
                        <span style={{ fontSize: 12, color: T.muted }}>—</span>
                      )}
                    </SmartTableTD>
                    <SmartTableTD theme={T}>
                      {call.lead?.lead_id ? (
                        <span
                          style={{
                            fontSize: 12.5,
                            fontWeight: 700,
                            color: LEAD_T.green,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          #{call.lead.lead_id}
                        </span>
                      ) : (
                        <span style={{ fontSize: 12, color: T.muted }}>—</span>
                      )}
                    </SmartTableTD>
                    <SmartTableTD theme={T}>
                      {call.lead?.id ? (
                        <Typography variant="body2" sx={{ fontWeight: 600, color: LEAD_T.green, fontSize: 12.5 }}>
                          {call.lead?.name || 'Unknown'}
                        </Typography>
                      ) : (
                        <span style={{ fontSize: 12, color: T.muted }}>
                          {call.lead?.name || 'No lead'}
                        </span>
                      )}
                    </SmartTableTD>
                    <SmartTableTD theme={T}>
                      <span style={{ fontSize: 12, color: T.muted }}>—</span>
                    </SmartTableTD>
                    <SmartTableTD theme={T} wrap>
                      <UnlinkedCallStatusCell call={call} />
                    </SmartTableTD>
                    <SmartTableTD theme={T} wrap>
                      <div style={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                        <AgentChip
                          name={call.agent?.name}
                          assigned={Boolean(call.agent?.name)}
                          onClick={() => {
                            if (call.agent?.id) window.open(`/reports/agent/${call.agent.id}`, '_blank');
                          }}
                        />
                      </div>
                    </SmartTableTD>
                    <SmartTableTD theme={T}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsLead([call.id]);
                        }}
                        sx={{
                          fontSize: '0.75rem',
                          height: 28,
                          textTransform: 'none',
                          borderRadius: 2,
                          fontWeight: 600,
                          borderColor: T.green,
                          color: T.green,
                          '&:hover': {
                            backgroundColor: T.green,
                            color: T.white,
                            borderColor: T.green,
                          },
                        }}
                      >
                        Mark as Lead
                      </Button>
                    </SmartTableTD>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Box>
      ) : (
        <EmptyCallsPageCard
          title={searchKeyword ? 'No matching unlinked calls found' : 'No unlinked calls found'}
          description={
            searchKeyword
              ? "Try adjusting your search criteria or filters to find the unlinked calls you're looking for."
              : 'No unlinked calls are available for the selected time period. Try adjusting your date range or filters.'
          }
          isSearchActive={isSearchActive}
          compact={false}
        />
      )}

      <MarkAsLeadModal
        open={markAsLeadModal.open}
        onClose={handleCloseMarkAsLeadModal}
        assetContactMap={markAsLeadModal.assetContactMap}
        onSuccess={handleMarkAsLeadSuccess}
      />
    </Box>
  );
};

export default UnlinkedCallListView;
